import type { ServiceItem } from '@/cms/fields/types/checkout-page'
import { useIntl } from '@/intl'
import CheckoutSection from '@/scopes/checkout/components/CheckoutSection'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import {
  ItemOption,
  ItemOptionContent,
  ItemOptionControl,
  ItemOptionTrailing,
  ItemOptionWrapper,
  OptionItemsContainer,
  OptionPrice,
  OptionValue,
} from '@/scopes/checkout/components/services/GenericService'
import {
  ParticipantName,
  RoomName,
} from '@/scopes/checkout/components/services/ServiceComponents'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useCheckoutSid, useService } from '@/scopes/checkout/domain/hooks'

import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import {
  SpaceBetween,
  tk,
} from '@sunwebgroup/ui-components'
import {
  Banner,
  type BannerColors,
  ListItem,
  RadioButton,
  RadioButtonGroup,
  SwitchField,
  Tag,
} from '@sunwebgroup/ui-components/uds'
import { isEmpty, values } from 'ramda'
import * as React from 'react'
import { useSessionStorage } from 'react-use'
import { updatePerBookingService, updatePerParticipantService } from '../../domain/actions'
import { createAncillariesClickedEvent, createGenericServiceEvent } from '../../domain/analytics'
import { bannerColorMap, BannerType, type CheckoutServiceOption, ServiceType } from '../../domain/types'

export function TravelInsuranceService({ item }: { item: ServiceItem }) {
  const intl = useIntl()
  const sid = useCheckoutSid()
  const { service } = useService(ServiceType.TravelInsurance)
  const { service: perBookingService, setServiceValue } = useService(ServiceType.TravelInsurancePerBooking)
  const { componentTitle, componentDescription, popupTitle, popupDescription, popupReadMoreLabel, serviceConfigurations } = item

  const { showToggleForAllParticipants, toggleForAllParticipantsCheckedByDefault, toggleForAllParticipantsLabel } = serviceConfigurations || {}

  const { sendEvent } = useAnalytics()

  const [selectPerParticipantChecked, setSelectPerParticipantChecked] = useSessionStorage(
    `${sid}-travel-insurance-is-per-participant`,
    toggleForAllParticipantsCheckedByDefault,
  )

  const handleSwitchPerParticipantChange = React.useCallback(
    () => {
      setSelectPerParticipantChecked(!selectPerParticipantChecked)
      sendEvent(
        createGenericServiceEvent({
          description: 'Choose per participant',
          element: 'toggle',
          type: 'selectTravelInsurance',
          result: !selectPerParticipantChecked ? 'element selected' : 'element unselected',
        }),
      )

      if (!selectPerParticipantChecked || !perBookingService)
        return

      const participants = Object.entries(service?.participants || {}).sort(
        ([keyA], [keyB]) => keyA.localeCompare(keyB),
      )

      const selectedValue = participants.find(([, value]) => value?.selectedValue)?.[1]?.selectedValue

      if (!selectedValue)
        return

      setServiceValue(updatePerBookingService({ value: selectedValue })(perBookingService))
    },
    [service, perBookingService, setServiceValue, setSelectPerParticipantChecked, selectPerParticipantChecked, sendEvent],
  )

  React.useEffect(() => {
    if (!service)
      return
    if (service.participants && Object.keys(service.participants).length === 1) {
      setSelectPerParticipantChecked(false)
    }
  })
  useSendAncillaryAvailable(service, 'selectTravelInsurance')

  if (!service)
    return null

  return (
    <CheckoutSection
      title={componentTitle || intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_title' })}
      description={componentDescription}
      glyph={getIconForService(ServiceType.TravelInsurance)}
      moreInformationTitle={popupTitle}
      moreInformationDescription={popupDescription}
      moreInformationReadMoreLabel={popupReadMoreLabel}
    >
      <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
        {showToggleForAllParticipants && service.participants && Object.keys(service.participants).length > 1 && (
          <SwitchField
            checked={selectPerParticipantChecked}
            label={toggleForAllParticipantsLabel || intl.formatMessage({ id: 'checkout_steps_ancillaries_generic_service_applies_to_all_participants' })}
            onCheckedChange={handleSwitchPerParticipantChange}
            variant="transparent"
          />
        )}
        {selectPerParticipantChecked ? <TravelInsuranceServicePerParticipant /> : <TravelInsuranceServicePerBooking />}
      </SpaceBetween>
    </CheckoutSection>
  )
}

function TravelInsuranceServicePerParticipant() {
  const { service, setServiceValue } = useService(ServiceType.TravelInsurance)
  const { sendEvent } = useAnalytics()

  const handleValueChange = React.useCallback(
    (participantId: string, value: string) => {
      if (!service)
        return
      setServiceValue(updatePerParticipantService({ participantId, value })(service))
      const optionClicked = service.options[value]
      sendEvent(
        createAncillariesClickedEvent({
          result: optionClicked?.price || 0,
          type: optionClicked?.label ? `${optionClicked?.label} for participant ${participantId}` : '',
          description: 'selectTravelInsurance',
          element: 'radiobutton',
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  if (!service)
    return null

  const { options, rooms, participants, banners } = service

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
      {rooms.map(({ id: roomId, name: roomName, participantIds }) => (
        <SpaceBetween key={roomId} direction="y" spacing={tk('spacing_md')}>
          {rooms.length > 1 && roomName
            ? (
                <RoomName as="p" $variant="body-sm-bold">
                  {roomName}
                </RoomName>
              )
            : null}
          {participantIds.map((participantId) => {
            const participant = participants?.[participantId]
            if (!participant || participant.availableOptions.length === 0) {
              return null
            }

            return (
              <ParticipantRow
                {...participant}
                key={participantId}
                options={options}
                onValueChange={handleValueChange}
                showName={values(participants).length > 1}
              />
            )
          })}
        </SpaceBetween>
      ))}
      {isEmpty(rooms)
      && Object.values(participants ?? {}).map(participant => (
        <ParticipantRow {...participant} key={participant.id} options={options} onValueChange={handleValueChange} showName={values(participants).length > 1} />
      ))}
      {banners.map(({ title, text, type = 'info' }, index) => {
        return (
          <Banner
            key={`banner-${index}`}
            color={bannerColorMap[type as BannerType] as BannerColors}
            text={title ?? ''}
            size={'M' as any}
            supportingText={text}
          />
        )
      },
      )}
    </SpaceBetween>
  )
}

function ParticipantRow({
  id,
  options,
  availableOptions,
  selectedValue,
  name,
  onValueChange,
  showName = true,
}: {
  id: string
  options: Record<string, CheckoutServiceOption>
  availableOptions: string[]
  name: string
  selectedValue: string | null
  onValueChange: (participantId: string, value: string) => void
  showName?: boolean
}) {
  const intl = useIntl()

  const handleValueChange = React.useCallback(
    (value: string) => {
      onValueChange(id, value)
    },
    [id, onValueChange],
  )

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
      {showName && (
        <ParticipantName>
          {name}
        </ParticipantName>
      )}
      <RadioButtonGroup value={selectedValue ?? undefined} onValueChange={handleValueChange}>
        <OptionItemsContainer>
          {(availableOptions ?? []).map((optionId) => {
            const { value, label, badge, price } = options[optionId]
            return (
              <ItemOptionWrapper key={value}>
                <ListItem variant="outlined">
                  <ItemOption>
                    <ItemOptionControl>
                      <RadioButton value={value} />
                    </ItemOptionControl>
                    <ItemOptionContent>
                      {badge && <Tag variant="success-tinted" size="M">{badge}</Tag>}
                      <OptionValue>{label}</OptionValue>
                    </ItemOptionContent>
                    <ItemOptionTrailing>
                      <OptionPrice>
                        {intl.formatCurrency(price, price < 0)}
                      </OptionPrice>
                    </ItemOptionTrailing>
                  </ItemOption>
                </ListItem>
              </ItemOptionWrapper>
            )
          },
          )}
        </OptionItemsContainer>
      </RadioButtonGroup>
    </SpaceBetween>
  )
}

function TravelInsuranceServicePerBooking() {
  const intl = useIntl()
  const { service, setServiceValue } = useService(ServiceType.TravelInsurancePerBooking)
  const { sendEvent } = useAnalytics()

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updatePerBookingService({ value })(service))
      const optionClicked = service.options.find(option => option.value === value)
      sendEvent(
        createAncillariesClickedEvent({
          result: (Object.keys(service.participants).length) * (optionClicked?.price || 0),
          type: optionClicked?.label || '',
          description: 'selectTravelInsurance',
          element: 'radiobutton',
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  if (!service)
    return null

  const { options, selectedValue, banners } = service

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
      <RadioButtonGroup value={selectedValue ?? undefined} onValueChange={handleValueChange}>
        <OptionItemsContainer>
          {options.map(({ label, badge, price, value, priceBreakdown }) => (
            <ItemOptionWrapper key={value}>
              <ListItem variant="outlined">
                <ItemOption>
                  <ItemOptionControl>
                    <RadioButton value={value} />
                  </ItemOptionControl>
                  <ItemOptionContent>
                    {badge && <Tag variant="success-tinted" size="M">{badge}</Tag>}
                    <OptionValue>{label}</OptionValue>
                  </ItemOptionContent>
                  <ItemOptionTrailing>
                    {priceBreakdown && priceBreakdown.length > 0
                      ? (
                          priceBreakdown?.map(({ ageCategory, price: pricePerAgeCategory }) => (
                            <OptionPrice key={ageCategory}>
                              {intl.formatMessage({
                                id: `checkout_steps_ancillaries_travel_insurance_age_category_${ageCategory}`,
                              })}
                              {' '}
                              {intl.formatMessage({ id: 'checkout_steps_price_per_person' }, { price: intl.formatCurrency(pricePerAgeCategory, pricePerAgeCategory < 0) })}
                            </OptionPrice>
                          ))
                        )
                      : (
                          <OptionPrice>
                            {intl.formatMessage({ id: 'checkout_steps_price_per_person' }, { price: intl.formatCurrency(price, price < 0) })}
                          </OptionPrice>
                        )}
                  </ItemOptionTrailing>
                </ItemOption>
              </ListItem>
            </ItemOptionWrapper>
          ))}
        </OptionItemsContainer>
      </RadioButtonGroup>
      {banners.map(({ title, text, type = 'info' }, index) => {
        return (
          <Banner
            key={`banner-${index}`}
            color={bannerColorMap[type as BannerType] as BannerColors}
            text={title ?? ''}
            size={'M' as any}
            supportingText={text}
          />
        )
      },
      )}
    </SpaceBetween>
  )
}
